/**
 * ✨ Ask AI — review dialog for `suggestCalibreLineage` (calibre lineage
 * Phase 5, design memo §7.1).
 *
 * Two modes, one dialog:
 *   - an existing line (`lineage` set): its members, structure locked — the
 *     design's "Refresh dates";
 *   - a candidate cluster from Find revision lines (`lineage` null): the curator
 *     creates the line from the dialog before any row can be applied.
 *
 * The #723 preservation pattern, applied to lineage facts:
 *   - before the run, a line with curated revisions shows a Cancel / Proceed
 *     warning;
 *   - the result is guarded again here against the values loaded NOW
 *     (guardLineageSuggestion), and every withheld proposal is listed;
 *   - each row's write is merged onto the stored calibre by
 *     revisionFieldsForMember / revisionFieldsForCurrent, which never change a
 *     curator value and never clear anything.
 *
 * Low-confidence rows are held back behind a toggle, and nothing is written
 * without its own Apply click.
 */
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import {
  getCalibresForManufacturer,
  saveLineage,
  setCalibreRevision,
  type CalibreLineageEntry,
} from '../../services/calibreLineage';
import {
  dedupeConflicts,
  describeConflict,
  guardLineageSuggestion,
  revisionFieldsForCurrent,
  revisionFieldsForMember,
  type AppliedRevisionFields,
} from '../../services/lineageSuggestionGuard';
import {
  suggestCalibreLineageWithAI,
  type CustomCalibreEntry,
  type LineageCuratorConflict,
  type LineageSuggestionConfidence,
  type LineageSuggestionMember,
  type SuggestCalibreLineageResult,
} from '../../services/firebase';
import { mixedCalibreNumbers, mixedNumbersText } from '../../services/revisionClusters';

interface Props {
  manufacturerId: string;
  /** The existing line, or null for a candidate cluster. */
  lineage: CalibreLineageEntry | null;
  /** Cluster members (cluster mode), or extra candidates to consider with a line. */
  calibreIds: string[];
  /** Line label to offer when creating a line from a cluster. */
  seedLabel?: string;
  /** The calibres as currently loaded (members + candidates), for the pre-run warning. */
  calibres: CustomCalibreEntry[];
  onClose: () => void;
  /** Called after any write, so the host reloads. */
  onApplied: () => void;
}

/** A revision carries a curator value the AI must keep. */
export function isCurated(c: Pick<CustomCalibreEntry, 'revisionLabel' | 'activeFromYear' | 'activeUntilYear' | 'activeDatesSource' | 'isCurrent' | 'isLineagePlaceholder'>): boolean {
  const curatorYears =
    c.activeDatesSource !== 'ai' && (typeof c.activeFromYear === 'number' || typeof c.activeUntilYear === 'number');
  return !!c.revisionLabel || curatorYears || c.isCurrent === true || c.isLineagePlaceholder === true;
}

function errorText(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return String(err);
}

const years = (from?: number, until?: number) =>
  typeof from === 'number' || typeof until === 'number' ? `${from ?? '?'}–${until ?? ''}` : '';

/** What a row would change on the stored calibre, in words. Empty = nothing. */
function changesOf(stored: CustomCalibreEntry, f: AppliedRevisionFields): string[] {
  const out: string[] = [];
  if (stored.calibreLineageId !== f.calibreLineageId) out.push('joins the line');
  if ((stored.revisionLabel ?? '') !== (f.revisionLabel ?? '')) out.push(`label ${f.revisionLabel}`);
  if (stored.activeFromYear !== f.activeFromYear || stored.activeUntilYear !== f.activeUntilYear) {
    out.push(`years ${years(f.activeFromYear, f.activeUntilYear)}`);
  }
  if (!!stored.isCurrent !== !!f.isCurrent) out.push('current');
  return out;
}

const confidenceVariant = (c: LineageSuggestionConfidence) =>
  c === 'high' ? 'success' : c === 'medium' ? 'warning' : 'default';

export function LineageSuggestionDialog({
  manufacturerId,
  lineage,
  calibreIds,
  seedLabel,
  calibres: initialCalibres,
  onClose,
  onApplied,
}: Props) {
  const [hint, setHint] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SuggestCalibreLineageResult | null>(null);
  const [stored, setStored] = useState<Map<string, CustomCalibreEntry>>(
    () => new Map(initialCalibres.map((c) => [c.id, c])),
  );
  const [showLow, setShowLow] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const curatedCount = useMemo(
    () => initialCalibres.filter((c) => (lineage ? c.calibreLineageId === lineage.id : true) && isCurated(c)).length,
    [initialCalibres, lineage],
  );
  const nameOf = (id: string) => stored.get(id)?.name ?? id;

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const raw = await suggestCalibreLineageWithAI({
        manufacturerId,
        ...(lineage ? { lineageId: lineage.id } : {}),
        ...(calibreIds.length ? { calibreIds } : {}),
        ...(hint.trim() ? { additionalContext: hint.trim() } : {}),
      });
      // Guard again against the values as they are NOW, not as the server read them.
      const fresh = new Map((await getCalibresForManufacturer(manufacturerId)).map((c) => [c.id, c]));
      setStored(fresh);
      const guarded = guardLineageSuggestion(raw, [...fresh.values()], lineage?.id ?? null);
      setResult({ ...guarded, curatorConflicts: dedupeConflicts(guarded.curatorConflicts) });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setRunning(false);
    }
  };

  const write = async (key: string, calibreId: string, fields: AppliedRevisionFields, okText: string) => {
    setBusy(key);
    try {
      await setCalibreRevision(calibreId, fields);
      const prev = stored.get(calibreId);
      if (prev) {
        const next = new Map(stored);
        // Mirror the write locally; a current flag moves off any other member.
        if (fields.isCurrent) {
          for (const [id, c] of next) if (id !== calibreId && c.calibreLineageId === fields.calibreLineageId && c.isCurrent) next.set(id, { ...c, isCurrent: undefined });
        }
        next.set(calibreId, {
          ...prev,
          calibreLineageId: fields.calibreLineageId,
          revisionLabel: fields.revisionLabel,
          activeFromYear: fields.activeFromYear,
          activeUntilYear: fields.activeUntilYear,
          isCurrent: fields.isCurrent,
          isLineagePlaceholder: fields.isLineagePlaceholder,
          activeDatesSource: fields.activeDatesSource,
          activeDatesConfidence: fields.activeDatesConfidence,
        });
        setStored(next);
      }
      toast.success(okText);
      onApplied();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const isRevisionLine = result?.verdict === 'revision-line' || result?.verdict === 'mixed';
  // The primary line, then every SEPARATE line the AI proposed beside it.
  const lines: LineProposal[] = result
    ? [
        {
          key: 'primary',
          members: result.members,
          current: result.current,
          successorNote: result.successorNote,
          baseLabel: result.baseLabel,
        },
        ...(result.additionalLines ?? []).map((l, i) => ({ key: `line-${i + 1}`, ...l })),
      ]
    : [];
  const allMembers = lines.flatMap((l) => l.members);
  const lowCount = allMembers.filter((m) => m.confidence === 'low').length;
  const conflicts = result
    ? dedupeConflicts([...result.curatorConflicts, ...(result.additionalLines ?? []).flatMap((l) => l.curatorConflicts)])
    : [];

  return (
    <Modal
      isOpen
      onClose={onClose}
      dismissOnBackdrop={false}
      title={lineage ? `Ask AI — ${lineage.baseLabel}` : 'Ask AI — candidate revision line'}
      size="xl"
    >
      <div className="space-y-4">
        {!result && (
          <>
            {curatedCount > 0 && (
              <div role="alert" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                This line has {curatedCount} curated revision{curatedCount === 1 ? '' : 's'}. AI suggestions may change{' '}
                {curatedCount === 1 ? 'it' : 'them'}. Proceed?
                <span className="block text-xs text-amber-800 mt-1">
                  Curator labels, years, the current flag and placeholders are kept: any suggestion that would change one is
                  withheld and listed.
                </span>
              </div>
            )}
            <label className="block text-sm">
              <span className="font-medium text-gray-700">Hint for the AI (optional)</span>
              <textarea
                rows={2}
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                placeholder="e.g. -2 replaced -1 in 1995"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={running}>
                Cancel
              </Button>
              <Button onClick={run} loading={running}>
                {curatedCount > 0 ? 'Proceed' : 'Ask AI'}
              </Button>
            </div>
          </>
        )}

        {result && (
          <>
            <div className="text-sm">
              <Badge variant={isRevisionLine ? 'info' : 'default'}>{result.verdict}</Badge>{' '}
              <span className="text-gray-700">{result.verdictReason}</span>
            </div>

            {lines.map((line, i) => (
              <LineCard
                key={line.key}
                line={line}
                heading={i === 0 ? (lines.length > 1 ? 'Line 1' : undefined) : `Line ${i + 1} — separate line`}
                lineage={i === 0 ? lineage : null}
                seedLabel={i === 0 ? seedLabel || line.baseLabel || '' : line.baseLabel || ''}
                canCreate={i === 0 ? isRevisionLine : true}
                manufacturerId={manufacturerId}
                stored={stored}
                nameOf={nameOf}
                showLow={showLow}
                busy={busy}
                setBusy={setBusy}
                write={write}
                onApplied={onApplied}
              />
            ))}
            {lowCount > 0 && (
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={showLow} onChange={(e) => setShowLow(e.target.checked)} />
                Show {lowCount} low-confidence row{lowCount === 1 ? '' : 's'}
              </label>
            )}

            {conflicts.length > 0 && (
              <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                <p className="font-medium mb-1">Kept your values ({conflicts.length})</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {conflicts.map((c, i) => (
                    <li key={i}>{describeConflict(c, nameOf)}</li>
                  ))}
                </ul>
              </div>
            )}

            {(result.knownMissingRevisions.length > 0 || result.stemAliases.length > 0) && (
              <div className="text-xs text-gray-600 space-y-1">
                {result.knownMissingRevisions.length > 0 && (
                  <p>
                    <span className="font-medium">Revisions not in the catalogue (information only):</span>{' '}
                    {result.knownMissingRevisions
                      .map((r) => [r.label, years(r.activeFromYear, r.activeUntilYear)].filter(Boolean).join(' '))
                      .join('; ')}
                  </p>
                )}
                {result.stemAliases.length > 0 && (
                  <p>
                    <span className="font-medium">Also known as:</span> {result.stemAliases.join(', ')}
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end pt-2 border-t">
              <Button variant="secondary" onClick={onClose} disabled={!!busy}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** One proposed line: the primary one, or a separate line beside it. */
interface LineProposal {
  key: string;
  members: LineageSuggestionMember[];
  current: SuggestCalibreLineageResult['current'];
  successorNote?: string;
  baseLabel?: string;
  /** Why the AI split this line off (separate lines only). */
  lineReason?: string;
  curatorConflicts?: LineageCuratorConflict[];
}

interface LineCardProps {
  line: LineProposal;
  heading?: string;
  /** The existing line this card reviews (the primary card of a line run), else null: create the line first. */
  lineage: CalibreLineageEntry | null;
  seedLabel: string;
  /** A "Create line" control is offered (the primary card only when the verdict is a revision line). */
  canCreate: boolean;
  manufacturerId: string;
  stored: Map<string, CustomCalibreEntry>;
  nameOf: (id: string) => string;
  showLow: boolean;
  busy: string | null;
  setBusy: (key: string | null) => void;
  write: (key: string, calibreId: string, fields: AppliedRevisionFields, okText: string) => Promise<void>;
  onApplied: () => void;
}

/**
 * The review of ONE proposed line: create it (when new), append its successor
 * note, apply member rows, mark its current revision. Each card holds its own
 * line id, so a split proposal (ETA 6498 / 6498-1 + a separate 6498-2) is
 * created and applied line by line.
 */
function LineCard({
  line,
  heading,
  lineage,
  seedLabel,
  canCreate,
  manufacturerId,
  stored,
  nameOf,
  showLow,
  busy,
  setBusy,
  write,
  onApplied,
}: LineCardProps) {
  const [lineId, setLineId] = useState<string | null>(lineage?.id ?? null);
  const [label, setLabel] = useState(seedLabel);
  const [lineDescription, setLineDescription] = useState(lineage?.description ?? '');
  const k = (s: string) => `${line.key}:${s}`;

  const createLine = async () => {
    setBusy(k('create'));
    try {
      const id = await saveLineage({ baseLabel: label, manufacturerId });
      setLineId(id);
      toast.success('Revision line created');
      onApplied();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  // Append (never replace) the AI's successor note to the line's description.
  const addSuccessorNote = async (note: string) => {
    if (!lineId) return;
    setBusy(k('note'));
    try {
      const next = lineDescription.trim() ? `${lineDescription.trim()} ${note}` : note;
      await saveLineage({ baseLabel: lineage?.baseLabel ?? label, manufacturerId, description: next }, lineId);
      setLineDescription(next);
      toast.success('Added to the line description');
      onApplied();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const { members, successorNote } = line;
  const mixedNumbers = mixedCalibreNumbers(members.map((m) => nameOf(m.calibreId)));
  const visible = members.filter((m) => showLow || m.confidence !== 'low');
  const current = line.current && (showLow || line.current.confidence !== 'low') ? line.current : null;

  // A separate line whose every member was withheld by the guard has nothing to create or apply.
  if (line.key !== 'primary' && members.length === 0) return null;

  return (
    <section className={heading ? 'space-y-3 rounded-lg border border-gray-200 p-3' : 'space-y-3'} aria-label={heading}>
      {heading && (
        <div className="text-sm">
          <span className="font-semibold text-gray-900">{heading}</span>
          {line.lineReason && <span className="block text-xs text-gray-600">{line.lineReason}</span>}
        </div>
      )}

      {!lineId && canCreate && (
        <div className="flex items-end gap-2 rounded border border-gray-200 p-3">
          <label className="block text-sm flex-1">
            <span className="font-medium text-gray-700">New revision line</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ETA Calibre 2824"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <Button onClick={createLine} loading={busy === k('create')} disabled={!label.trim() || !!busy}>
            Create line
          </Button>
        </div>
      )}
      {!lineId && canCreate && (
        <p className="text-xs text-gray-500">Create the line first; then apply the revisions you agree with.</p>
      )}

      {mixedNumbers.length > 0 && (
        <p role="alert" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {mixedNumbersText(mixedNumbers)} Apply only one number&apos;s rows to this line.
        </p>
      )}

      {successorNote && (
        <div className="flex items-start justify-between gap-2 rounded border border-gray-200 p-3 text-sm">
          <div>
            <span className="font-medium text-gray-900">Successor note:</span>{' '}
            <span className="text-gray-700">{successorNote}</span>
            <div className="text-xs text-gray-500">Appended to the line description; your text is kept.</div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={!lineId || lineDescription.includes(successorNote) || !!busy}
            loading={busy === k('note')}
            onClick={() => addSuccessorNote(successorNote)}
          >
            {lineDescription.includes(successorNote) ? 'In description' : 'Add to description'}
          </Button>
        </div>
      )}

      {members.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="py-1 pr-2">#</th>
              <th className="py-1 pr-2">Calibre</th>
              <th className="py-1 pr-2">Will write</th>
              <th className="py-1 pr-2">Confidence</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((m) => {
              const s = stored.get(m.calibreId);
              if (!s) return null;
              const fields = lineId ? revisionFieldsForMember(s, lineId, m) : null;
              const changes = fields ? changesOf(s, fields) : [];
              return (
                <tr key={m.calibreId} className="align-top">
                  <td className="py-1.5 pr-2 tabular-nums text-gray-500">{m.order}</td>
                  <td className="py-1.5 pr-2">
                    <div className="font-medium text-gray-900">{s.name}</div>
                    {m.evidence && <div className="text-xs text-gray-500">{m.evidence}</div>}
                  </td>
                  <td className="py-1.5 pr-2 text-xs text-gray-700">
                    {fields ? (changes.length ? changes.join(', ') : <span className="text-gray-400">No change</span>) : (
                      <span className="text-gray-500">
                        {[m.revisionLabel && `label ${m.revisionLabel}`, years(m.activeFromYear, m.activeUntilYear)].filter(Boolean).join(', ') || '—'}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2">
                    <Badge variant={confidenceVariant(m.confidence)}>{m.confidence}</Badge>
                  </td>
                  <td className="py-1.5 text-right">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!fields || changes.length === 0 || !!busy}
                      loading={busy === k(m.calibreId)}
                      onClick={() => fields && write(k(m.calibreId), m.calibreId, fields, `${s.name} updated`)}
                    >
                      Apply
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {current && (() => {
        const s = stored.get(current.calibreId);
        if (!s) return null;
        const already = !!s.isCurrent;
        return (
          <div className="flex items-start justify-between gap-2 rounded border border-gray-200 p-3 text-sm">
            <div>
              <span className="font-medium text-gray-900">Current revision: {s.name}</span>{' '}
              <Badge variant={confidenceVariant(current.confidence)}>{current.confidence}</Badge>
              {current.reasoning && <div className="text-xs text-gray-500">{current.reasoning}</div>}
            </div>
            <Button
              size="sm"
              variant="secondary"
              disabled={!lineId || already || s.calibreLineageId !== lineId || !!busy}
              loading={busy === k('current')}
              onClick={() => lineId && write(k('current'), s.id, revisionFieldsForCurrent(s, lineId), `${s.name} marked current`)}
            >
              {already ? 'Already current' : 'Mark current'}
            </Button>
          </div>
        );
      })()}
      {current && lineId && stored.get(current.calibreId)?.calibreLineageId !== lineId && (
        <p className="text-xs text-gray-500">Apply that calibre&apos;s row first; then it can be marked current.</p>
      )}
    </section>
  );
}
